import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import Select from '@components/fields/Select';
import styles from '@components/forms/ArchiveJourneyForm.module.css';
import Form from '@components/forms/Form';
import {
  GetExpertJourneysQuery,
  JourneyExpertArchivedReasonEnum,
  JourneyExpertArchivedToggleDocument,
  JourneyExpertArchivedToggleMutation,
  JourneyExpertArchivedToggleMutationVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { getCareProfileName } from '@utils/modelUtils';

type Journey = NonNullable<NonNullable<NonNullable<GetExpertJourneysQuery['expertJourneys']['edges']>[number]>['node']>;

type FormData = {
  other?: string | null | undefined;
  reason: JourneyExpertArchivedReasonEnum | null;
};

type Props = {
  journey?: Journey;
  onCancel: () => void;
  onError: () => void;
  onSuccess: () => void;
};

const ArchiveJourneyForm = ({ journey, onCancel, onError, onSuccess }: Props) => {
  const defaultValues: FormData = {
    other: '',
    reason: null
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [journeyExpertArchivedToggle] = useMutation<
    JourneyExpertArchivedToggleMutation,
    JourneyExpertArchivedToggleMutationVariables
  >(JourneyExpertArchivedToggleDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      other: yup.string().when('reason', ([reason], schema) => {
        if (reason === JourneyExpertArchivedReasonEnum.other) {
          return schema.required(intl.formatMessage({ id: 'errors.journeyExpertArchive.other.blank' }));
        }
        return schema.nullable();
      }),
      reason: yup
        .string()
        .oneOf(Object.values(JourneyExpertArchivedReasonEnum))
        .required(intl.formatMessage({ id: 'errors.journeyExpertArchive.reason.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const reasonValue = form.watch('reason');

  const onSubmit = useCallback(async () => {
    if (journey) {
      setBaseErrors([]);
      return journeyExpertArchivedToggle({
        variables: {
          expertArchived: true,
          expertArchivedReason: {
            expertArchivedReasonOption: form.getValues().reason,
            other: form.getValues().other
          },
          journeyId: journey?.id
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.journeyExpertArchivedToggle,
          intl,
          onError,
          onSuccess: () => {
            if (response.data?.journeyExpertArchivedToggle?.journey) {
              onSuccess();
            }
          },
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.journey'
        });
      });
    }
  }, [form, intl, journey, journeyExpertArchivedToggle, onError, onSuccess]);

  const reasonOptions = useMemo(() => {
    const options = [
      JourneyExpertArchivedReasonEnum.client_disinterest,
      JourneyExpertArchivedReasonEnum.death_of_loved_one,
      JourneyExpertArchivedReasonEnum.situation_resolved,
      JourneyExpertArchivedReasonEnum.other
    ];
    return options.map(option => ({
      label: intl.formatMessage({ id: `models.journey.expertArchivedReasons.${option}` }),
      value: option
    }));
  }, [intl]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <div>
        <FormattedMessage
          id="forms.expertArchiveJourney.journeyName"
          values={{ name: getCareProfileName({ intl, journey }) }}
        />
      </div>
      <div>
        <FormattedMessage id="forms.expertArchiveJourney.clientName" values={{ name: journey?.owner.fullName }} />
      </div>
      <BaseErrors errors={baseErrors} />
      <Select<FormData>
        control={form.control}
        error={form.formState.errors.reason}
        label={intl.formatMessage({ id: 'forms.expertArchiveJourney.reasonLabel' })}
        name="reason"
        options={reasonOptions}
        required
      />

      <div className={reasonValue !== JourneyExpertArchivedReasonEnum.other ? styles.hiddenInput : ''}>
        <Input<FormData>
          autofocus={false}
          control={form.control}
          error={form.formState.errors.other}
          label={intl.formatMessage({ id: 'forms.expertArchiveJourney.otherLabel' })}
          name="other"
          required
        />
      </div>
      <FormButtons hookForm={form} onCancel={onCancel} />
    </Form>
  );
};
export default ArchiveJourneyForm;
